import React, { useState } from 'react'
import { faPen } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import palette from 'src/designSystem/variables/palette'
import { FlexRow, FlexAlign } from 'src/components/Reusable/Flex'
import { ConfirmCancelButtons } from 'src/components/Reusable/Buttons/ConfirmCancelButtons'
import { EditableWrapper } from './EditableField.styles'

// Save and Cancel for an editor that draws its own buttons.
export interface EditableFieldActions {
  onSave: (e: React.MouseEvent) => void
  onCancel: () => void
  isSaving: boolean
}

interface EditableFieldProps {
  renderView: () => React.ReactNode
  // A function draws its own Save and Cancel, such as inside the editor's card.
  children:
    | React.ReactNode
    | ((actions: EditableFieldActions) => React.ReactNode)
  onEdit?: () => void
  onCancel?: () => void
  onSave?: (e: React.FormEvent | React.MouseEvent) => void
  isEditing?: boolean
  isSaving?: boolean
  disabled?: boolean
  fullWidth?: boolean
  // Where the check/cross sit against a multi-row editor. Centred by default,
  // which is right for a single control; an editor that stacks rows -- a select
  // with a note under it, say -- passes FLEX_START so the buttons line up with
  // the first row instead of floating halfway down the stack.
  actionsAlign?: FlexAlign
  // Pins the hover pencil to the row's right edge rather than after the value,
  // so a column of fields keeps its pencils in a line.
  isIconAtEnd?: boolean
}

export const EditableField = ({
  children,
  renderView,
  isEditing: isEditingProp,
  onEdit,
  onCancel,
  onSave,
  isSaving = false,
  disabled = false,
  fullWidth = false,
  actionsAlign = FlexAlign.CENTER,
  isIconAtEnd = false,
}: EditableFieldProps): JSX.Element => {
  const [internalIsEditing, setInternalIsEditing] = useState(false)
  const isEditing =
    isEditingProp !== undefined ? isEditingProp : internalIsEditing

  const [isHovered, setIsHovered] = useState(false)

  const handleStartEdit = () => {
    if (isEditingProp === undefined) {
      setInternalIsEditing(true)
    }
    onEdit?.()
  }

  const handleCancel = () => {
    if (isEditingProp === undefined) {
      setInternalIsEditing(false)
    }
    onCancel?.()
  }

  const handleSave = (e: React.FormEvent | React.MouseEvent) => {
    onSave?.(e)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
      handleSave(e)
    }
    if (e.key === 'Escape') {
      e.stopPropagation()
      handleCancel()
    }
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  if (isEditing) {
    return (
      <div
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        style={fullWidth ? { width: '100%' } : undefined}
      >
        {typeof children === 'function' ? (
          children({ onSave: handleSave, onCancel: handleCancel, isSaving })
        ) : (
          <FlexRow
            isFullWidth={fullWidth}
            gap={8}
            align={actionsAlign}
            justify={FlexAlign.FLEX_START}
          >
            <div style={{ flex: 1 }}>{children}</div>
            <ConfirmCancelButtons
              onConfirm={handleSave}
              onCancel={handleCancel}
              isSaving={isSaving}
              justify={FlexAlign.FLEX_START}
            />
          </FlexRow>
        )}
      </div>
    )
  }

  if (disabled) {
    return <>{renderView()}</>
  }

  return (
    <EditableWrapper
      onClick={handleStartEdit}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {renderView()}
      {isHovered && (
        <FontAwesomeIcon
          icon={faPen}
          style={{
            fontSize: '10px',
            marginLeft: isIconAtEnd ? 'auto' : '4px',
            color: palette.neutral[400],
          }}
        />
      )}
    </EditableWrapper>
  )
}
